import { Body, IconButton, Tooltip } from '@assembly-js/design-system';
import { useTheme } from '@mui/material/styles';
import {
  SendButtonProps,
  useMessageComposer,
  useMessageComposerHasSendableData,
} from 'stream-chat-react';

import { MAX_CHARACTERS_LIMIT } from 'src/constants/messageConsts';
import {
  isAssistantStatusBusy,
  textMentionsAssistant,
} from 'src/features/assistant/assistantMention';

interface CustomSendButtonProps extends SendButtonProps {
  aiAssistantStatus?: {
    status: string;
    metadata?: {
      tool?: string;
      query?: string;
    };
    messageId?: string;
  } | null;
}

// created while referencing the default SendButton component
// https://github.com/GetStream/stream-chat-react/blob/master/src/components/MessageInput/SendButton.tsx
export const CustomSendButton = ({
  sendMessage,
  aiAssistantStatus,
  ...props
}: CustomSendButtonProps) => {
  const theme = useTheme();
  const accentColor = theme.palette.primary.main;

  const hasSendableData = useMessageComposerHasSendableData();

  // Get message composer to check mentions
  const { textComposer } = useMessageComposer();

  // The send path adds the assistant mention for typed "@Assembly", so the text
  // alone decides whether this message will trigger the assistant.
  const isAssistantMentioned = textMentionsAssistant(textComposer.text);

  const isAssistantBusy = isAssistantStatusBusy(aiAssistantStatus?.status);

  // Check if message exceeds character limit
  const exceedsCharacterLimit = textComposer.text.length > MAX_CHARACTERS_LIMIT;

  // Disable if no sendable data OR (assistant mentioned AND busy) OR exceeds character limit
  const isDisabled =
    !hasSendableData ||
    (isAssistantMentioned && isAssistantBusy) ||
    exceedsCharacterLimit;
  const showTooltip =
    hasSendableData && isAssistantMentioned && isAssistantBusy;

  const button = (
    <IconButton
      className="p-[5px] rounded-md disabled:border-transparent"
      icon="SendFilled"
      aria-label="send"
      disabled={isDisabled}
      onClick={sendMessage}
      style={{
        backgroundColor: !isDisabled ? accentColor : undefined,
        borderColor: !isDisabled ? accentColor : undefined,
      }}
      {...props}
    />
  );

  // Wrap in tooltip when assistant is busy and mentioned
  if (showTooltip) {
    return (
      <Tooltip
        content={
          <Body size="xs">
            The Assembly Assistant is currently busy, please wait or cancel.
          </Body>
        }
        position="top"
      >
        <span>{button}</span>
      </Tooltip>
    );
  }

  return button;
};
